import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LabelsSelectors, useLabelsSelectors, SelectedLabels } from '@kosmos/shared';

import {
  useImportTopologyMutation,
  useLazyCheckIfTopologyExistQuery,
} from "../../../app/apiSlice";
import { useLazyGetCatalogQuery } from '../../libraries/DataLibrarySlice';
import { RenderLoading } from "../../libraries/components/sourceExterne/DataSourceExterneDetail";
import { useDispatch } from "react-redux";
import { setToast } from "../../../app/appSlice";
import { FileUpload } from "primereact/fileupload";
import { useImportTopology } from "./UseImportTopology";
import { chooseOptions } from "../NavBar";
import { ContentErrorNode, INodeVerification } from "./NodeNotExisting";
import nodeExistOrHasExisted from "../../../utils/nodeExistOrHasExisted";
import { useAppSelector } from "../../../hooks/hooks";
import { initImportTopology, setTopologyImported } from "../TreatmentSlice";
import "../Treatment.scss";
import { Dropdown } from 'primereact/dropdown';
import { envTemplate } from '../../../components/environnement/envTemplate';
import { IEnvironnements } from '../../../components/environnement/environnements';
import { addExistingLabel } from '../../../components/labels/ImportObjectWithExistingLabels';

interface IFormValues {
  name: string;
  description: string;
  environment: IEnvironnements;
  label: SelectedLabels;
}

//component that allows user to import new topologies with json files

export const ImportTopologyForm = ({ onClose }: { onClose: () => void }) => {

  const { onSelectFile, clearImport, fileUploadRef } = useImportTopology();
  const [checkIfTopologyExist, { data: topologyExist }] =
  useLazyCheckIfTopologyExistQuery();
  const environnements = useAppSelector((state) => state.app.config.environnements)
  const { selectedLabels, setSelectedLabels, labels } = useLabelsSelectors();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<IFormValues>({
    mode: 'onSubmit', reValidateMode: 'onChange', defaultValues: {
      name: '', description: '', environment: environnements && environnements.length > 0 ? environnements[0] : undefined, label: selectedLabels
    }});

  const { t } = useTranslation();
  const [importTopology, { ...statusTopo }] = useImportTopologyMutation();
  const [getCatalog, { data: catalog, ...statusCatalog }] =
      useLazyGetCatalogQuery();
  const [unKnowNode, setUnknowNode] = useState<INodeVerification[]>([]);
  const history = useNavigate();
  const dispatch = useDispatch();
  const topology = useAppSelector(
    (state) => state.treatment.importedTopology.importedTopology
  );
  const fileName = useAppSelector(
    (state) => state.treatment.importedTopology.fileName
  );

  // Topology Updated only when a new file import occur
  useEffect(() => {
    if (topology) {
      checkIfTopologyExist({ name: topology.name });
      setValue("description", topology.description);
      setValue("name", topology.name);
      setSelectedLabels(addExistingLabel(topology.metadatas, labels))
      if (!catalog) {
        getCatalog({ runtimeType: topology.runtimeMode });
      }
      setUnknowNode([]);
    }
  }, [topology, labels]);

  //Submit topology with name and description possibly updated
  const onSubmit = async (data: IFormValues) => {
    try {
      const topologyExist = await checkIfTopologyExist({
          name: data.name,
      }).unwrap();
      if (topologyExist.topologyExist) {
        dispatch(
            setToast({
                severity: 'error',
                title: `Erreur`,
                content:`Le nom ${data.name} existe déjà`,
                life: 3000,
            })
        );
        return
      }

    } catch {
      dispatch(
        setToast({
            severity: 'error',
            title: `Erreur`,
            content:`Une erreur est survenue, veuillez contacter votre administrateur`,
            life: 3000,
        })
    );
    return
    }

    if (catalog && topology) {
      const nodeExistInCatalog: INodeVerification[] = nodeExistOrHasExisted(
        catalog,
        topology.graph
      );
      if (nodeExistInCatalog.length === 0) {
        const importedTopology = {
          ...topology,
          name: data.name,
          description: data.description,
          environnement: data.environment.code,
          metadatas: {
            ...topology.metadatas,
            ...selectedLabels
          }
        };
        try {
          const data = await importTopology({ ...importedTopology }).unwrap();
          dispatch(initImportTopology());
          history(`/treatments/editor?t=${data.id}&v=${data.version}`);
          onClose();
        } catch {
          dispatch(
            setToast({
              title: "Import d'un traitement",
              content: `Echec de l'import du traitement ${topology.name}`,
              life: 3000,
              severity: "error",
            })
          );
        }
      } else {
        setUnknowNode(nodeExistInCatalog);
      }
    }
  };

  //label error when a file is incorrect
  const GetFormErrorMessage = (name: string) => {
    return (
      // @ts-ignore
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  return (
    <>
      {" "}
      {!topology && (
        <section className="flex justify-content-center mt-2 mb-2">
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            multiple={false}
            accept=".json"
            maxFileSize={1000000}
            onSelect={onSelectFile}
            disabled={!!topology}
            chooseOptions={chooseOptions}
          />
        </section>
      )}
      {fileName && (
        <div className="flex">
          <div className="flex mr-2 flex align-items-center">
            <span className="titleList mr-1">Fichier sélectionné :</span>{" "}
            <span className="contentList">{fileName}</span>
          </div>
          <div>
            <Button
              icon="pi pi-trash"
              className="p-button-text"
              onClick={() => {
                clearImport();
                dispatch(
                  setTopologyImported({
                    fileName: "",
                    importedTopology: null,
                  })
                );
                setUnknowNode([]);
              }}
            />
          </div>
        </div>
      )}
      {fileName && catalog && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="topologyForm p-fluid mb-4"
        >
          {statusTopo.isSuccess && (
            <Message
              severity={"success"}
              text={t("Nouveau traitement importé")}
            />
          )}
          {unKnowNode.length > 0 && (
            <div>
              <Message
                severity="error"
                className="w-full"
                content={<ContentErrorNode node={unKnowNode} />}
              />
            </div>
          )}
          <div className="p-field mb-2 mt-3">
            <span className="p-label">
              <label
                htmlFor="name"
                className={classNames({
                  "p-error": errors.name,
                  titleList: !errors.name,
                })}
              >
                {t("label.title")}*
              </label>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: `${t("label.title")} ${t("label.required-m")}.`,
                }}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    {...field}
                    className={classNames({ "p-invalid": fieldState.invalid || topologyExist?.topologyExist })}
                    onBlur={(e) => {
                      checkIfTopologyExist({ name: e.target.value })
                      setValue("name", e.target.value);
                    }}
                    maxLength={128}
                  />
                )}
              />
            </span>
            {GetFormErrorMessage("name")}
            {topologyExist?.topologyExist && (
              <small className="p-error">
                {t("message.treatment.already-existed")}
              </small>
            )}
          </div>

          <div className="p-field">
            <span className="p-label">
              <label
                htmlFor="description"
                className={classNames({
                  "p-error": errors.description,
                  titleList: !errors.description,
                })}
              >
                {t("label.description")}
              </label>
              <Controller
                name="description"
                control={control}
                rules={{}}
                render={({ field }) => (
                  <InputText
                    id={field.name}
                    {...field}
                    maxLength={128}
                  />
                )}
              />
            </span>
            {GetFormErrorMessage("description")}
          </div>

          <div className="p-field mt-4">
            <span className="p-label">
              <label
                  htmlFor="environment"
                  className={classNames({'p-error': errors.environment})}
                  style={{fontWeight: 'bold'}}
              >
                {t('label.environment')}*
              </label>
              <Controller
                name="environment"
                control={control}
                rules={{
                  required: `${t('label.environment')} ${t('label.required-m')}.`,
                }}
                render={({field}) => (
                    <Dropdown
                      id={field.name} value={field.value}
                      options={environnements}
                      onChange={(e) => field.onChange(e.value)}
                      optionLabel="name"
                      placeholder="Selectionner un environnement"
                      className="w-full"
                      itemTemplate={envTemplate}
                      valueTemplate={envTemplate}
                    />
                )}
              />
            </span>
          </div>

          <LabelsSelectors values={selectedLabels}/>

          <div className="bottomButtons">
            <div className="flexButton">
              <Button
                type="button"
                label={"Annuler"}
                onClick={() => {
                  onClose();
                  clearImport();
                }}
                className="p-button-text"
              />
              <Button
                type="submit"
                loading={statusTopo.isLoading}
                label={t("Valider")}
                disabled={false}
              />
            </div>
          </div>
        </form>
      )}
      {statusCatalog.isError && (
        <Message severity="error" text="Erreur de chargement des données" />
      )}
      {statusCatalog.isLoading && <RenderLoading />}
    </>
  );
};
